import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button, Card, Form, Row, Col, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { appPaths } from 'config';
import {
  createVM,
  setupOpenClaw,
  setupHermes,
  getOpenClawStatus
} from 'helpers/requests/vms';
import { handleUnexpectedStatusCode } from 'helpers/errors';

const PROVIDERS = {
  openclaw: [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' },
    { value: 'google', label: 'Google' }
  ],
  hermes: [
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'openai', label: 'OpenAI' }
  ]
};

const MODELS = {
  anthropic: [
    { value: 'anthropic/claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'anthropic/claude-opus-4-6', label: 'Claude Opus 4.6' }
  ],
  openai: [
    { value: 'openai/gpt-5.4', label: 'GPT-5.4' },
    { value: 'openai/gpt-5.4-pro', label: 'GPT-5.4 Pro' }
  ],
  google: [
    { value: 'google/gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
    { value: 'google/gemini-3-flash-preview', label: 'Gemini 3 Flash' }
  ]
};

const CreateVMForm = () => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [creatingVm, setCreatingVm] = useState(false);
  const [vmId, setVmId] = useState(null);
  const [vmReady, setVmReady] = useState(false);

  // Wizard step: 1 = agent choice, 2 = provider/model/key, 3 = telegram
  const [step, setStep] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState(null); // 'openclaw' | 'hermes'

  // Agent config
  const [modelProvider, setModelProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [model, setModel] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [isCustomModel, setIsCustomModel] = useState(false);

  // Telegram
  const [telegramToken, setTelegramToken] = useState('');

  // Setup state
  const [configSaved, setConfigSaved] = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  const [errors, setErrors] = useState({});

  const savedConfig = useRef(null);
  const isMounted = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Reset model when provider changes
  useEffect(() => {
    setModel('');
    setCustomModel('');
    setIsCustomModel(false);
  }, [modelProvider]);

  // Poll for Cloud Desktop readiness once created
  useEffect(() => {
    if (!vmId) return;
    let cancelled = false;

    const poll = async () => {
      for (let i = 0; i < 60; i++) {
        if (cancelled) return;
        try {
          const r = await getOpenClawStatus(vmId);
          if (r.success) {
            if (!cancelled && isMounted.current) setVmReady(true);
            return;
          }
        } catch (e) {
          console.error(e);
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [vmId]);

  // When Cloud Desktop becomes ready, send config
  useEffect(() => {
    if (!vmReady || !vmId || !configSaved) return;

    const sendConfig = async () => {
      // Always send OpenClaw base setup (gateway token)
      const openclawResult = await setupOpenClaw(
        vmId,
        savedConfig.current?.agent === 'openclaw' ? savedConfig.current : {}
      );

      // If Hermes was chosen, also send Hermes config
      if (savedConfig.current?.agent === 'hermes') {
        const hermesResult = await setupHermes(vmId, savedConfig.current);
        if (!isMounted.current) return;

        if (hermesResult.success) {
          setSetupDone(true);
          toast.success('Cloud Desktop created and Hermes configured.');
        } else {
          toast.error(
            'Cloud Desktop created but Hermes setup failed. You can configure it later.'
          );
        }
      } else if (!isMounted.current) {
        return;
      } else if (savedConfig.current) {
        // OpenClaw was configured
        setSetupDone(true);
        if (openclawResult.success) {
          toast.success('Cloud Desktop created and OpenClaw configured.');
        } else {
          toast.error(
            'Cloud Desktop created but OpenClaw setup failed. You can configure it later.'
          );
        }
      } else {
        // Skipped
        setSetupDone(true);
        toast.success('Cloud Desktop created.');
      }

      navigate(appPaths.vms.detail.replace(':id', vmId));
    };
    sendConfig();
  }, [vmReady, vmId, configSaved]);

  const handleCreateVM = async () => {
    setSubmitting(true);
    const r = await createVM({ name: name || undefined });
    if (r.success) {
      setCreatingVm(true);
      setVmId(r.response.data.id);
    } else {
      if (r.response?.status === 400 && r.response?.data?.message) {
        toast.error(r.response.data.message);
      } else {
        handleUnexpectedStatusCode(r.expectedStatusCode, r.response?.status);
      }
      setSubmitting(false);
    }
  };

  const handleSaveConfig = () => {
    const finalModel = isCustomModel ? customModel : model;

    const config = {
      agent: selectedAgent,
      api_key: apiKey,
      provider: modelProvider,
      model: finalModel
    };

    if (selectedAgent === 'openclaw' && telegramToken.trim()) {
      config.channels = {
        telegram: { botToken: telegramToken.trim() }
      };
    }

    if (selectedAgent === 'hermes' && telegramToken.trim()) {
      config.telegram_token = telegramToken.trim();
    }

    savedConfig.current = config;
    setConfigSaved(true);
  };

  const handleSkip = () => {
    savedConfig.current = null;
    setConfigSaved(true);
  };

  const handleProviderContinue = () => {
    const newErrors = {};
    if (!modelProvider) newErrors.modelProvider = 'Select a provider';
    if (!apiKey) newErrors.apiKey = 'API key is required';
    const finalModel = isCustomModel ? customModel : model;
    if (!finalModel) newErrors.model = 'Select or enter a model';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Skip Telegram step for now — go straight to saving config
    handleSaveConfig();
  };

  const agentLabel = selectedAgent === 'hermes' ? 'Hermes' : 'OpenClaw';

  // -- Create form (before Cloud Desktop is created) --
  if (!creatingVm) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <Card style={{ width: '100%', maxWidth: '400px' }}>
          <Card.Body>
            <h5 className="mb-3">Launch a Cloud Desktop</h5>
            <Form.Group className="mb-3">
              <Form.Label>
                Name <span className="text-muted fs--1">(optional)</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. my-cloud-desktop"
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={64}
              />
            </Form.Group>
            <Button
              variant="primary"
              className="w-100"
              onClick={handleCreateVM}
              disabled={submitting}
            >
              Launch
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // -- Config saved, waiting for Cloud Desktop --
  if (configSaved && !vmReady) {
    return (
      <Modal show backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" className="mb-3" />
          <h5>Setting up your Cloud Desktop...</h5>
          <p className="text-muted mb-0">
            {savedConfig.current
              ? `${agentLabel} will be configured once the Cloud Desktop is ready.`
              : 'Your desktop will be ready shortly.'}
          </p>
        </Modal.Body>
      </Modal>
    );
  }

  // -- Config saved, Cloud Desktop ready, finalizing --
  if (configSaved && vmReady && !setupDone) {
    return (
      <Modal show backdrop="static" keyboard={false} centered>
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" className="mb-3" />
          <h5>
            {savedConfig.current
              ? `Configuring ${agentLabel}...`
              : 'Almost there...'}
          </h5>
        </Modal.Body>
      </Modal>
    );
  }

  // -- Wizard modal --
  return (
    <Modal show backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header>
        <Modal.Title>
          {!vmReady ? (
            <span>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating Cloud Desktop...
            </span>
          ) : (
            'Cloud Desktop Ready'
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Step 1: Agent choice */}
        {step === 1 && (
          <>
            <p className="text-muted mb-4">
              {!vmReady
                ? 'Your Cloud Desktop is starting up. Set up an AI agent while you wait.'
                : 'Your Cloud Desktop is ready. Set up an AI agent or go straight to your desktop.'}
            </p>
            <Row className="g-3">
              <Col sm={4}>
                <Card
                  className="h-100 border-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedAgent('openclaw');
                    setStep(2);
                  }}
                >
                  <Card.Body className="text-center py-4">
                    <FontAwesomeIcon
                      icon="robot"
                      size="2x"
                      className="text-primary mb-3"
                    />
                    <h6>Set up OpenClaw</h6>
                    <p className="text-muted fs--1 mb-0">
                      AI agent with web dashboard and messaging channels.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={4}>
                <Card
                  className="h-100 border-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedAgent('hermes');
                    setStep(2);
                  }}
                >
                  <Card.Body className="text-center py-4">
                    <FontAwesomeIcon
                      icon="terminal"
                      size="2x"
                      className="text-primary mb-3"
                    />
                    <h6>Set up Hermes</h6>
                    <p className="text-muted fs--1 mb-0">
                      AI agent with terminal interface and messaging channels.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col sm={4}>
                <Card
                  className="h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={handleSkip}
                >
                  <Card.Body className="text-center py-4">
                    <FontAwesomeIcon
                      icon="arrow-right"
                      size="2x"
                      className="text-muted mb-3"
                    />
                    <h6>Skip</h6>
                    <p className="text-muted fs--1 mb-0">
                      Go straight to your desktop. Set up any agent later.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Step 2: Provider + API Key + Model */}
        {step === 2 && (
          <>
            <h6 className="mb-3">Configure {agentLabel} — Model provider</h6>
            <Row className="g-2 mb-3">
              {(PROVIDERS[selectedAgent] || []).map(provider => (
                <Col xs="auto" key={provider.value}>
                  <Button
                    variant={
                      modelProvider === provider.value
                        ? 'primary'
                        : 'falcon-default'
                    }
                    onClick={() => {
                      setModelProvider(provider.value);
                      setErrors(prev => ({ ...prev, modelProvider: null }));
                    }}
                    type="button"
                  >
                    {provider.label}
                  </Button>
                </Col>
              ))}
            </Row>
            {errors.modelProvider && (
              <div className="text-danger fs--1 mb-3">
                {errors.modelProvider}
              </div>
            )}

            {modelProvider && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>API Key</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      autoComplete="off"
                      placeholder="Enter your API key"
                      value={apiKey}
                      onChange={e => {
                        setApiKey(e.target.value);
                        setErrors(prev => ({ ...prev, apiKey: null }));
                      }}
                      isInvalid={!!errors.apiKey}
                      style={
                        showApiKey
                          ? {}
                          : {
                              WebkitTextSecurity: 'disc',
                              textSecurity: 'disc'
                            }
                      }
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-50 translate-middle-y text-400 me-2"
                      style={{ zIndex: 5 }}
                      onClick={() => setShowApiKey(!showApiKey)}
                      type="button"
                    >
                      <FontAwesomeIcon
                        icon={showApiKey ? 'eye-slash' : 'eye'}
                      />
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {errors.apiKey}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Model</Form.Label>
                  {!isCustomModel ? (
                    <>
                      <Form.Select
                        value={model}
                        onChange={e => {
                          if (e.target.value === '__custom__') {
                            setIsCustomModel(true);
                            setModel('');
                          } else {
                            setModel(e.target.value);
                          }
                          setErrors(prev => ({ ...prev, model: null }));
                        }}
                        isInvalid={!!errors.model}
                      >
                        <option value="">Select a model...</option>
                        {MODELS[modelProvider]?.map(m => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                        <option value="__custom__">Custom model...</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.model}
                      </Form.Control.Feedback>
                    </>
                  ) : (
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="e.g. anthropic/claude-sonnet-4-6"
                        value={customModel}
                        onChange={e => {
                          setCustomModel(e.target.value);
                          setErrors(prev => ({ ...prev, model: null }));
                        }}
                        isInvalid={!!errors.model}
                      />
                      <Button
                        variant="falcon-default"
                        onClick={() => {
                          setIsCustomModel(false);
                          setCustomModel('');
                        }}
                        type="button"
                        title="Back to list"
                      >
                        <FontAwesomeIcon icon="list" />
                      </Button>
                    </div>
                  )}
                </Form.Group>
              </>
            )}
          </>
        )}

        {/* Step 3: Telegram */}
        {step === 3 && (
          <>
            <h6 className="mb-3">Connect Telegram</h6>
            <p className="text-muted fs--1 mb-3">
              Chat with your AI agent from Telegram. Create a bot with{' '}
              <strong>@BotFather</strong> and paste the token below.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Bot Token</Form.Label>
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Paste your BotFather token"
                value={telegramToken}
                onChange={e => setTelegramToken(e.target.value)}
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' }}
              />
              <Form.Text className="text-muted">
                Don't have one?{' '}
                <a
                  href="https://www.youtube.com/shorts/fdTDvhXllv0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch how to get a token
                </a>
              </Form.Text>
            </Form.Group>
          </>
        )}
      </Modal.Body>

      {/* Footer changes per step */}
      {step === 1 ? null : (
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button
              variant="link"
              className="text-muted p-0 fs--1"
              onClick={() => {
                if (step === 2) {
                  handleSkip();
                } else if (step === 3) {
                  handleSaveConfig();
                }
              }}
            >
              {step === 2
                ? 'Looking for a different provider? Skip'
                : 'Skip Telegram'}
            </Button>
            <div className="text-muted fs--2 mt-1">
              {step === 2
                ? 'You can set up any provider from your Cloud Desktop. Instructions are on the desktop.'
                : 'Looking for WhatsApp, Discord, or another channel? Set it up from your Cloud Desktop.'}
            </div>
          </div>
          <div className="d-flex gap-2">
            {step === 2 && (
              <Button
                variant="falcon-default"
                onClick={() => {
                  setSelectedAgent(null);
                  setModelProvider('');
                  setStep(1);
                }}
                type="button"
              >
                Back
              </Button>
            )}
            {step === 3 && (
              <Button
                variant="falcon-default"
                onClick={() => setStep(2)}
                type="button"
              >
                Back
              </Button>
            )}
            <Button
              variant="primary"
              onClick={step === 2 ? handleProviderContinue : handleSaveConfig}
            >
              {step === 2 ? 'Continue' : 'Go to desktop'}
            </Button>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CreateVMForm;
